import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useTenant } from './TenantContext';
import { canManageMenu, canManageStaff, canUpdateAvailability, ROLE_RESTAURANT_ADMIN, ROLE_STAFF, ROLE_FRANCHISEE } from '../utils/permissions';

const AuthContext = createContext({})

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const asUuid = (value) => {
  const text = `${value || ''}`?.trim();
  return UUID_RE?.test(text) ? text : null;
};

const restaurantIdFromUser = (authUser) =>
  asUuid(authUser?.user_metadata?.restaurant_id)
  || asUuid(authUser?.user_metadata?.invited_restaurant_id);

const normalizeMemberships = (rows) =>
  (Array.isArray(rows) ? rows : [])?.filter((row) => asUuid(row?.restaurant_id));

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const { restaurantId: tenantRestaurantId } = useTenant();
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [memberships, setMemberships] = useState([])
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async loadFromTables(userId) {
      const [profileResult, membershipResult] = await Promise.all([
        supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.maybeSingle(),
        supabase
          ?.from('restaurant_members')
          ?.select('id, user_id, restaurant_id, role')
          ?.eq('user_id', userId),
      ]);
      if (profileResult?.error) {
        console.error('Profile load error:', profileResult?.error);
      }
      if (membershipResult?.error) {
        console.error('Memberships load error:', membershipResult?.error);
      }
      return {
        profile: profileResult?.error ? null : (profileResult?.data || null),
        memberships: membershipResult?.error ? [] : normalizeMemberships(membershipResult?.data),
      };
    },

    async load(userId, authUser) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        let nextProfile = null;
        let nextMemberships = [];

        const { data: synced, error: syncError } = await supabase?.rpc('sync_my_restaurant_access');
        if (syncError) {
          if (syncError?.code !== 'PGRST202' && syncError?.code !== '42883') {
            console.error('Restaurant access sync error:', syncError);
          }
          const fallback = await profileOperations?.loadFromTables(userId);
          nextProfile = fallback?.profile;
          nextMemberships = fallback?.memberships;
        } else {
          nextProfile = synced?.profile || null;
          nextMemberships = normalizeMemberships(synced?.memberships);
          if (!nextProfile && !nextMemberships?.length) {
            const fallback = await profileOperations?.loadFromTables(userId);
            nextProfile = fallback?.profile;
            nextMemberships = fallback?.memberships;
          }
        }

        const metadataRestaurantId = restaurantIdFromUser(authUser);
        const dbRestaurantId =
          asUuid(nextProfile?.restaurant_id)
          || nextMemberships?.[0]?.restaurant_id
          || null;
        const resolvedRestaurantId = dbRestaurantId || metadataRestaurantId || null;
        const needsProfileBackfill =
          !!resolvedRestaurantId
          && nextProfile?.role !== 'super_admin'
          && asUuid(nextProfile?.restaurant_id) !== resolvedRestaurantId;

        const metaRole =
          authUser?.user_metadata?.role
          || authUser?.user_metadata?.invited_role
          || null;
        const resolvedRole =
          nextProfile?.role
          || (metaRole === ROLE_STAFF ? ROLE_STAFF : null)
          || (metaRole === 'super_admin' ? 'super_admin' : null)
          || ROLE_RESTAURANT_ADMIN;

        if (!nextProfile && authUser) {
          nextProfile = {
            id: userId,
            email: authUser?.email || '',
            full_name: authUser?.user_metadata?.full_name || '',
            restaurant_id: resolvedRestaurantId,
            role: resolvedRole,
          };
        } else if (nextProfile) {
          if (!nextProfile?.restaurant_id && resolvedRestaurantId) {
            nextProfile = { ...nextProfile, restaurant_id: resolvedRestaurantId };
          }
          if (!nextProfile?.role) {
            nextProfile = { ...nextProfile, role: resolvedRole };
          }
        }

        if (resolvedRestaurantId && !nextMemberships?.some((m) => m?.restaurant_id === resolvedRestaurantId)) {
          nextMemberships = [
            {
              id: `local-${resolvedRestaurantId}`,
              user_id: userId,
              restaurant_id: resolvedRestaurantId,
              role: nextProfile?.role === ROLE_STAFF ? ROLE_STAFF : ROLE_RESTAURANT_ADMIN,
            },
            ...nextMemberships,
          ];
        }

        if (resolvedRestaurantId && nextProfile && !nextProfile?.restaurants) {
          const { data: restaurantRow } = await supabase
            ?.from('restaurants')
            ?.select('id, slug, display_name')
            ?.eq('id', resolvedRestaurantId)
            ?.maybeSingle();
          if (restaurantRow) {
            nextProfile = {
              ...nextProfile,
              restaurants: restaurantRow,
              restaurant_slug: restaurantRow?.slug,
            };
          }
        }

        if (
          needsProfileBackfill
          && nextProfile
        ) {
          const { error: upsertError } = await supabase
            ?.from('user_profiles')
            ?.upsert({
              id: userId,
              email: nextProfile?.email || authUser?.email || '',
              full_name: nextProfile?.full_name || '',
              restaurant_id: resolvedRestaurantId,
              role: nextProfile?.role || ROLE_RESTAURANT_ADMIN,
            }, { onConflict: 'id' });
          if (upsertError) {
            console.error('Profile restaurant_id backfill error:', upsertError);
          }
        }

        if (nextProfile) setUserProfile(nextProfile);
        setMemberships(nextMemberships);
      } catch (error) {
        console.error('Profile load error:', error);
        // Keep restaurant admins usable (QR / editor) even if profile sync throws.
        if (authUser) {
          const fallbackRestaurantId = restaurantIdFromUser(authUser);
          const fallbackRole =
            authUser?.user_metadata?.role
            || authUser?.user_metadata?.invited_role
            || ROLE_RESTAURANT_ADMIN;
          setUserProfile({
            id: userId,
            email: authUser?.email || '',
            full_name: authUser?.user_metadata?.full_name || '',
            restaurant_id: fallbackRestaurantId,
            role: fallbackRole,
          });
          if (fallbackRestaurantId) {
            setMemberships([
              {
                id: `local-${fallbackRestaurantId}`,
                user_id: userId,
                restaurant_id: fallbackRestaurantId,
                role: fallbackRole === ROLE_STAFF ? ROLE_STAFF : ROLE_RESTAURANT_ADMIN,
              },
            ]);
          }
        }
      } finally {
        setProfileLoading(false);
      }
    },

    clear() {
      setUserProfile(null);
      setMemberships([]);
      setProfileLoading(false);
    }
  };

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null);

      if (session?.user) {
        // TOKEN_REFRESHED fires when the tab regains focus — skip redundant profile fetches
        const shouldLoadProfile =
          !event ||
          event === 'SIGNED_IN' ||
          event === 'INITIAL_SESSION' ||
          event === 'USER_UPDATED' ||
          event === 'PASSWORD_RECOVERY';
        if (shouldLoadProfile) {
          setProfileLoading(true);
        }
        setLoading(false);
        if (shouldLoadProfile) {
          profileOperations?.load(session?.user?.id, session?.user);
        }
      } else {
        setLoading(false);
        profileOperations?.clear();
      }
    }
  };

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()
      ?.then(({ data: { session } }) => {
        authStateHandlers?.onChange(null, session)
      })
      ?.catch((err) => {
        console.warn('Supabase getSession network error (non-fatal):', err?.message)
        setLoading(false)
      })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update(updates)
        ?.eq('id', user?.id)
        ?.select('*')
        ?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Derived convenience values
  const paramRestaurantId = asUuid(new URLSearchParams(location.search)?.get('restaurant_id'));
  const linkedRestaurantId =
    asUuid(userProfile?.restaurant_id)
    || memberships?.[0]?.restaurant_id
    || restaurantIdFromUser(user)
    || null;
  const metaRole =
    user?.user_metadata?.role
    || user?.user_metadata?.invited_role
    || null;
  const profileRole = userProfile?.role || metaRole || null;
  const isSuperAdmin = profileRole === 'super_admin';
  const isFranchisee = profileRole === ROLE_FRANCHISEE;
  // Restaurant admins: prefer their linked restaurant over whatever subdomain they opened.
  // Super admins: URL/tenant first so "Manage" / subdomain preview keeps working.
  const restaurantId = isSuperAdmin
    ? (paramRestaurantId || asUuid(tenantRestaurantId) || linkedRestaurantId || null)
    : (paramRestaurantId || linkedRestaurantId || asUuid(tenantRestaurantId) || null);
  const restaurantSlug = userProfile?.restaurants?.slug ?? userProfile?.restaurant_slug ?? null
  const restaurantDisplayName = userProfile?.restaurants?.display_name ?? userProfile?.full_name ?? null
  const currentMembership = memberships?.find((m) => m?.restaurant_id === restaurantId) || null
  const isRestaurantScopedRole =
    profileRole === ROLE_RESTAURANT_ADMIN
    || profileRole === ROLE_STAFF;
  const roleInCurrentRestaurant = currentMembership?.role
    || (linkedRestaurantId && linkedRestaurantId === restaurantId ? profileRole : null)
    || (isRestaurantScopedRole ? profileRole : null)
    || null
  // Access for QR / editor: membership, linked restaurant, or restaurant_admin/staff role.
  // Role also comes from invite metadata when profile rows are incomplete.
  const hasRestaurantAccess = isSuperAdmin
    || isFranchisee
    || !!currentMembership
    || !!linkedRestaurantId
    || (memberships?.length > 0)
    || isRestaurantScopedRole;
  const canAccessRestaurant = (targetRestaurantId) => {
    const target = asUuid(targetRestaurantId);
    if (isSuperAdmin) return true;
    if (isFranchisee) return true; // franchisee access is enforced by DB RLS
    if (!target) return hasRestaurantAccess;
    if (memberships?.some((m) => m?.restaurant_id === target)) return true;
    if (linkedRestaurantId && linkedRestaurantId === target) return true;
    // Restaurant admin/staff on their subdomain: tenant id is enough when profile link is missing
    if (isRestaurantScopedRole && asUuid(tenantRestaurantId) === target) {
      return !linkedRestaurantId || linkedRestaurantId === target;
    }
    return false;
  };
  const permissionSet = {
    canManageMenu: canManageMenu(roleInCurrentRestaurant, isSuperAdmin, isFranchisee),
    canManageStaff: canManageStaff(roleInCurrentRestaurant, isSuperAdmin, isFranchisee),
    canUpdateAvailability: canUpdateAvailability(roleInCurrentRestaurant, isSuperAdmin, isFranchisee),
    isRestaurantAdmin: isSuperAdmin || isFranchisee || roleInCurrentRestaurant === ROLE_RESTAURANT_ADMIN,
    isStaff: roleInCurrentRestaurant === ROLE_STAFF,
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    // Multi-restaurant helpers
    memberships,
    restaurantId,
    linkedRestaurantId,
    restaurantSlug,
    restaurantDisplayName,
    isSuperAdmin,
    isFranchisee,
    roleInCurrentRestaurant,
    hasRestaurantAccess,
    canAccessRestaurant,
    ...permissionSet,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
